export type AiModelBrand = {
  /** Stable identifier, also used as a React key. */
  id: string;
  /** Public-relative path to the optimized 3D brand render. */
  src: string;
  /** Accessible image label (the model/product name). */
  alt: string;
  /** Display name shown under the icon. */
  name: string;
  /** Smaller caption under the name (e.g. the maker). */
  sublabel: string;
  /**
   * Brand-tinted glow used by the floating pedestal. References a CSS theme
   * token (`--ai-model-glow-*` in globals.css) so the palette stays in one
   * place — see also the AI models band blooms.
   */
  glow: string;
  /** Intrinsic pixel dimensions — used for aspect ratio only. */
  width: number;
  height: number;
};

/**
 * The frontier AI models surfaced across the marketing site (Studio "AI models"
 * section and the bottom CTA background). Consume by mapping over this array and
 * wrapping each entry in a `FloatingIcon`, so new models stay a one-line edit.
 */
export const AI_MODEL_BRANDS: AiModelBrand[] = [
  {
    id: "openai",
    src: "/images/ai-models/openai.webp",
    alt: "OpenAI",
    name: "OpenAI",
    sublabel: "GPT-4o · o-series",
    glow: "var(--ai-model-glow-openai)",
    width: 512,
    height: 512,
  },
  {
    id: "claude",
    src: "/images/ai-models/claude.webp",
    alt: "Claude",
    name: "Claude",
    sublabel: "Anthropic",
    glow: "var(--ai-model-glow-claude)",
    width: 512,
    height: 512,
  },
  {
    id: "gemini",
    src: "/images/ai-models/gemini.webp",
    alt: "Gemini",
    name: "Gemini",
    sublabel: "Google",
    glow: "var(--ai-model-glow-gemini)",
    width: 512,
    height: 512,
  },
];

/** Select-field options (Studio) sourced from the brand registry. */
export const AI_MODEL_BRAND_OPTIONS = AI_MODEL_BRANDS.map((brand) => ({
  label: brand.name,
  value: brand.id,
}));

/** Look up a brand by id, falling back to the first entry for unknown ids. */
export const getAiModelBrand = (id: string): AiModelBrand =>
  AI_MODEL_BRANDS.find((brand) => brand.id === id) ?? AI_MODEL_BRANDS[0];
